import axios from 'axios'
import { useQuery } from '@tanstack/react-query'

import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import {
  type GuidanceApiResponse,
  type GuidanceStatus,
  isCompletedResponse,
  isFailedResponse,
  isProcessingResponse,
  isSkippedResponse,
} from '../../../shared/types'
import { getGuidanceV3 } from '../../apis/guidance'
import type { GuidanceSupportDataV3 } from '../../types'

// server response の生 shape が snake / camel いずれでも、 旧 field 名 (= summary)
// でも、 統一して GuidanceSupportDataV3 (= statement / context / 対象加算紐付け) に整形.
// context (= 確認の論拠 1 行) は statement 直下の従属行として描画する. 無ければ null.
const normalizeV3 = (raw: unknown): GuidanceSupportDataV3 => {
  const r = raw as Record<string, unknown>
  const rawCps = (r.checkPoints ?? r.check_points ?? []) as Array<
    Record<string, unknown>
  >
  return {
    version: 'v3',
    checkPoints: rawCps.map((cp) => ({
      statement: (cp.statement ?? cp.summary ?? '') as string,
      context: (cp.context ?? null) as string | null,
      related_kasan_type: (cp.related_kasan_type ?? null) as
        | GuidanceSupportDataV3['checkPoints'][number]['related_kasan_type']
        | null,
      // 既定 false. 本 field 導入前に生成済みの薬歴は key を持たない
      // (既存薬歴は作り直さない方針のため、 古い形は残り続ける).
      is_contraindicated: (cp.is_contraindicated ?? false) as boolean,
      // 患者への聞き方. β の版 (2.8.0〜) だけが返す. 無ければ key ごと持たない
      ...(typeof cp.question === 'string' && cp.question
        ? { question: cp.question }
        : {}),
    })),
    isInitial: (r.isInitial ?? r.is_initial) as boolean | undefined,
    previousKeywords: (r.previousKeywords ?? r.previous_keywords) as
      | GuidanceSupportDataV3['previousKeywords']
      | undefined,
    requestId: (r.requestId ?? r.request_id) as string | undefined,
    // 監査 (β). 足す前に生成済みの薬歴は key を持たないので null に寄せる.
    audit: (r.audit ?? null) as GuidanceSupportDataV3['audit'],
    auditHits: (r.auditHits ?? r.audit_hits ?? null) as
      | GuidanceSupportDataV3['auditHits'],
  }
}

const POLL_INTERVAL_MS = 2000

interface ParsedGuidance {
  status: GuidanceStatus | 'mismatch'
  data: GuidanceSupportDataV3 | null
  errorMessage: string | null
  skippedReason: 'patient_not_linked' | null
  // mismatch のとき back が返してきた generated_version (= 'v2')
  generatedVersion: 'v2' | null
}

const parse = (raw: GuidanceApiResponse | unknown): ParsedGuidance => {
  if (isCompletedResponse(raw) && raw.data.version === 'v3') {
    return {
      status: 'completed',
      data: normalizeV3(raw.data),
      errorMessage: null,
      skippedReason: null,
      generatedVersion: null,
    }
  }
  if (isProcessingResponse(raw)) {
    return {
      status: 'processing',
      data: null,
      errorMessage: null,
      skippedReason: null,
      generatedVersion: null,
    }
  }
  if (isFailedResponse(raw)) {
    return {
      status: 'failed',
      data: null,
      errorMessage: raw.error.message,
      skippedReason: null,
      generatedVersion: null,
    }
  }
  if (isSkippedResponse(raw)) {
    return {
      status: 'skipped',
      data: null,
      errorMessage: null,
      skippedReason: raw.reason,
      generatedVersion: null,
    }
  }
  return {
    status: 'failed',
    data: null,
    errorMessage: '想定外のレスポンス形式',
    skippedReason: null,
    generatedVersion: null,
  }
}

export interface UseGuidanceQueryV3Result {
  status: GuidanceStatus | 'mismatch' | null
  data: GuidanceSupportDataV3 | null
  errorMessage: string | null
  skippedReason: 'patient_not_linked' | null
  generatedVersion: 'v2' | null
  isLoading: boolean
  refetch: () => Promise<unknown>
}

// V3 専用 envelope 取得 hook.
// 409 mismatch (= back の guidance_data が v2) を catch して status='mismatch' に
// 正規化する. caller (= GuidancePrepPaneV3 / shared dispatcher) は status で
// version-mismatch fallback を render.
export const useGuidanceQueryV3 = (
  yakurekiId: string | undefined
): UseGuidanceQueryV3Result => {
  const query = useQuery<ParsedGuidance>({
    queryKey: [...queryKeys.guidance.detail(yakurekiId ?? ''), 'v3'],
    queryFn: async () => {
      try {
        const response = await getGuidanceV3(yakurekiId as string)
        return parse(response.data)
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 409) {
          const body = err.response.data as {
            error?: string
            generated_version?: string
          }
          return {
            status: 'mismatch' as const,
            data: null,
            errorMessage: null,
            skippedReason: null,
            generatedVersion: (body.generated_version ?? 'v2') as 'v2',
          }
        }
        throw err
      }
    },
    enabled: !!yakurekiId,
    staleTime: 1000 * 60 * 30,
    refetchInterval: (q) => {
      const state = q.state.data as ParsedGuidance | undefined
      return state?.status === 'processing' ? POLL_INTERVAL_MS : false
    },
    refetchIntervalInBackground: false,
    retry: (failureCount, error) => {
      if (axios.isAxiosError(error)) {
        const code = error.response?.status
        if (code === 403 || code === 409) return false
      }
      return failureCount < 3
    },
  })

  const status = query.data?.status ?? null
  const isPolling = status === 'processing'

  return {
    status,
    data: query.data?.data ?? null,
    errorMessage: query.data?.errorMessage ?? null,
    skippedReason: query.data?.skippedReason ?? null,
    generatedVersion: query.data?.generatedVersion ?? null,
    isLoading: query.isLoading || isPolling,
    refetch: query.refetch,
  }
}
